import { useEffect, useState } from 'react';

/**
 * The docked bars, the floating create button a list screen draws at its bottom right, and mobile
 * web's own bottom bar (`MobileNavigation`, `.mobile-bottom-nav`) with its centre ＋, which stands
 * above the bar's own top (I-1152). In the app the tab bar is native and `--shell-inset-bottom`
 * says where it is, so this finds no web bar there.
 *
 * F-0781 (h): and the shell's bottom region (`[data-bottom-bar-slot]`), which sits on the tab bar,
 * so a toast or a pill stands on the as-if bar or the email bar rather than over it. An empty slot
 * has no height and is passed over.
 */
const FLOATING_SELECTOR =
  '[data-docked-bar], [data-bottom-bar-slot], .phone-floating-create-button, .mobile-bottom-nav, .mobile-bottom-nav a, .mobile-bottom-nav button';

/** Frames a reading must hold before it is taken as settled and the following stops (~0.5 s). */
export const SETTLED_FRAMES = 30;

/** What can start a bar moving: the window, the keyboard's viewport, a transition, focus. */
const WINDOW_EVENTS = ['resize', 'focusin', 'focusout'] as const;
const DOCUMENT_EVENTS = [
  'transitionrun',
  'transitionend',
  'animationstart',
  'animationend',
  'scroll',
] as const;

function readRise(): number {
  const tops = Array.from(document.querySelectorAll<HTMLElement>(FLOATING_SELECTOR))
    .map((bar) => bar.getBoundingClientRect())
    // A bar that yields the dock is `hidden`: it has no box and stands under nothing.
    .filter((box) => box.height > 0)
    .map((box) => box.top);
  return tops.length === 0 ? 0 : Math.max(0, window.innerHeight - Math.min(...tops));
}

/**
 * The `bottom` of a phone notice (a toast, a pill) given {@link useDockedBarsRise}: 12 above whichever
 * is higher, the app's own tab bar (`--shell-inset-bottom`) or what the page measured. **The one
 * offset for every phone notice** — a notice that writes its own `bottom` from the shell inset alone
 * sits on mobile web's bar, where the inset is 0 (I-1152). `phoneNoticeBottom.guard.test.ts` holds it.
 */
export function phoneNoticeBottom(rise: number): string {
  return `calc(${dockedBarsTop(rise)} + 12px)`;
}

/**
 * The `bottom` that rests on the highest bar: the app's tab bar or what the page measured, with no
 * gap. What phone notices stand 12 above ({@link phoneNoticeBottom}), and where a band that is a
 * bar's row (the Options pill from 1.5 × type, I-1314) sits.
 */
export function dockedBarsTop(rise: number): string {
  return `max(var(--shell-inset-bottom, 0px), ${rise}px)`;
}

/**
 * How far the docked bars (`DockedBar`: the comment bar on every item, the Answer bar once an option
 * is picked) and the floating create button rise above the viewport's bottom, as the highest of their
 * tops says; 0 with none. Whatever floats over the page (the Options pill, the Take-it-back toast)
 * has to stand on top of them rather than under them: the comment bar is z-40 and covered a pill at
 * `bottom: 12px` whole, and the toast touched the create button (found drawing the sheets, IU-8).
 *
 * A bar moves by a `transform` (the keyboard, the web's bottom bar settling) and grows when its
 * editor mounts, and neither tells an observer, so a move is followed frame by frame. Measured once
 * or by `ResizeObserver`, the pill was left under a bar that then moved (flaky
 * `decision-answer-phone.spec.ts`).
 *
 * ⛔ **Followed, then let go (I-0781 follow-up).** It used to read on every frame for as long as a
 * pill or a toast was up, which on a decision waiting for an answer is as long as the reader reads:
 * a layout read 60 times a second, measured as 18.7 s of a phone page's work in 61 s of its clock
 * (0.3 s without it). Now anything that can start a move (a resize, the keyboard's viewport, a
 * transition or animation, focus, a scroll, a change to the page) wakes the following, and it stops
 * once the reading has held for {@link SETTLED_FRAMES} frames.
 */
export function useDockedBarsRise(enabled = true): number {
  const [rise, setRise] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let frame = 0;
    let still = 0;
    let last = Number.NaN;
    const follow = () => {
      const value = readRise();
      setRise(value);
      still = value === last ? still + 1 : 0;
      last = value;
      frame = still < SETTLED_FRAMES ? requestAnimationFrame(follow) : 0;
    };
    const wake = () => {
      still = 0;
      if (!frame) frame = requestAnimationFrame(follow);
    };
    follow();

    for (const name of WINDOW_EVENTS) window.addEventListener(name, wake);
    for (const name of DOCUMENT_EVENTS) {
      document.addEventListener(name, wake, { capture: true, passive: true });
    }
    window.visualViewport?.addEventListener('resize', wake);
    window.visualViewport?.addEventListener('scroll', wake);
    // A bar mounting, an editor growing in it, `hidden` set on one: a change to the page.
    const pageChanges = typeof MutationObserver === 'undefined' ? null : new MutationObserver(wake);
    pageChanges?.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
    return () => {
      cancelAnimationFrame(frame);
      for (const name of WINDOW_EVENTS) window.removeEventListener(name, wake);
      for (const name of DOCUMENT_EVENTS) {
        document.removeEventListener(name, wake, { capture: true });
      }
      window.visualViewport?.removeEventListener('resize', wake);
      window.visualViewport?.removeEventListener('scroll', wake);
      pageChanges?.disconnect();
    };
  }, [enabled]);
  return rise;
}
